import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../api/firebase/references";
import { DocWriteBackStatus } from "../../enums/evBvWriteBackStatus";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { RootState } from "../../redux/store/store";
import { Delete } from "../../svg/delete";
import DocIcon from "../../svg/doc-icon";
import { getDeleteDocumentEventNameWithFallback } from "../../utils/deleteDocumentEventTypeName";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { removeDocumentFromJcode } from "../../utils/updateJcodesWithCorrectDocRef";

import { apiWorkerManager } from "../../workerManagers/workerManager";
interface NarLetterDeleteProps {
  title: string;
  onDelete?: () => void;
  exitDirection?: "left" | "right";
  docType?: string;
  docId?: string;
  onClick?: () => void;
  docUploadStatus?: string;
}

const NarLetterDelete = ({
  title,
  onDelete,
  exitDirection = "left",
  docType,
  docId,
  onClick,
  docUploadStatus,
}: NarLetterDeleteProps) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgID = getOrgIdForFetchExternalWorklist();

  const handleDelete = async () => {
    setIsDeleting(true);
    const newAuthEntriesData = removeDocumentFromJcode(
      singleMedicalPaOrder?.auth_on_file?.auth_entries ?? [],
      docId ?? "",
      docType ?? "",
    );

    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.medicalPaOrders(),
        singleMedicalPaOrder?.id ?? "",
        {
          auth_on_file: {
            ...singleMedicalPaOrder?.auth_on_file,
            auth_entries: newAuthEntriesData,
          },
        },
      );
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name: getDeleteDocumentEventNameWithFallback(docType ?? ""),
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          doc_type: docType ?? "",
        },
      });
      setIsDeleting(false);
      setIsExiting(true);
      setTimeout(() => {
        onDelete?.();
      }, 300);
    } catch (error) {
      setIsDeleting(false);
      console.error(error);
    }
  };

  const exitDirectionClass = useMemo(() => {
    return exitDirection === "left" ? "-translate-x-4" : "translate-x-4";
  }, [exitDirection]);
  return (
    <div
      className={`flex flex-row items-center justify-center gap-1 rounded-md bg-lightGray p-2 transition-all duration-300 ease-in-out ${isExiting ? `${exitDirectionClass} opacity-0` : "opacity-100"} `}
    >
      {isDeleting ? (
        <>
          <div className="flex flex-row items-center justify-center gap-2">
            <SpinningLoader />
            <div className="text-sm">Deleting...</div>
          </div>
        </>
      ) : (
        <>
          <div onClick={onClick} className="flex flex-row items-center gap-2">
            <div>
              <DocIcon />
            </div>
            <div
              className={`mr-1 border-gray-300 pr-2 text-tiny font-semibold ${
                docUploadStatus === DocWriteBackStatus.NOT_INITIATED ||
                docUploadStatus === DocWriteBackStatus.ERROR
                  ? "border-r-2"
                  : ""
              }`}
            >
              {title}
            </div>
          </div>
          {docUploadStatus !== DocWriteBackStatus.IN_PROGRESS && (
            <div className="icon cursor-pointer" onClick={handleDelete}>
              <Delete height={14} width={14} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NarLetterDelete;
